/**
 * Timezone conversion utilities for recurring weekly time slots.
 *
 * All availability / class-section times are stored as UTC in the database
 * using TEXT "HH:MM" format with an integer day_of_week. The helpers
 * themselves are convention-agnostic — they preserve whatever integer the
 * caller supplies — but every consumer in this codebase uses 0=Sun (matches
 * JS `Date#getDay()` and the DB CHECK 0..6).
 *
 * These helpers convert between a user's local time and UTC, handling
 * day-of-week shifts that occur when the offset crosses midnight.
 *
 * DST note: by default we sample the offset at a fixed reference date
 * (2024-07-15, a Monday in summer). Pass `refDate` to `getUtcOffsetMinutes`
 * when you need DST-correct sampling at a specific session date — for
 * recurring weekly slots the default is the pragmatic choice ("9 AM ET"
 * stays at 9 AM local across DST transitions).
 */

export type WeeklySlot = {
  dayOfWeek: number; // 0..6 (0=Sun in every caller in this repo)
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
};

// ─── internal helpers ────────────────────────────────────────────────

/**
 * Reference date: July 15 2024 12:00 UTC (a Monday, mid-month noon to avoid
 * month-boundary and midnight edge cases in day-of-month comparisons).
 * Used for abbreviation display and as a stable DST reference.
 */
const REF_DATE = new Date(Date.UTC(2024, 6, 15, 12, 0, 0));

/**
 * Get the UTC offset in minutes for a given IANA timezone.
 * Positive = east of UTC (e.g. Asia/Tokyo +540), negative = west (e.g. America/New_York -240).
 *
 * Works by formatting the reference date in the target timezone, parsing the
 * parts back into a UTC Date, and comparing the two epoch values.
 *
 * `refDate` controls which moment the offset is sampled at — this matters for
 * DST zones (e.g., America/New_York is -240 in July but -300 in January). For
 * weekly recurring slots, pass the date of the specific session instance so a
 * "9 AM ET" class stays at 9 AM ET on both sides of the DST transition. The
 * default (REF_DATE = 2024-07-15) is a stable summer reference for callers
 * that don't have a session-specific date in hand.
 */
function getUtcOffsetMinutes(iana: string, refDate: Date = REF_DATE): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(refDate);
  const get = (type: string) =>
    parseInt(parts.find((p) => p.type === type)!.value, 10);

  // Rebuild the local wall-clock as if it were UTC
  const localAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') === 24 ? 0 : get('hour'), // midnight can be reported as 24
    get('minute'),
    get('second')
  );

  return (localAsUtc - refDate.getTime()) / 60_000;
}

function parseTime(hhmm: string): { hours: number; minutes: number } {
  const [h, m] = hhmm.split(':').map(Number);
  return { hours: h, minutes: m };
}

function formatTime(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function wrapDay(day: number): number {
  return ((day % 7) + 7) % 7;
}

function convertSlot(
  dayOfWeek: number,
  startTime: string,
  endTime: string,
  offsetMinutes: number // positive = add to UTC to get local, so subtract to go local→UTC
): WeeklySlot {
  const start = parseTime(startTime);
  const end = parseTime(endTime);

  const startTotalMinutes = start.hours * 60 + start.minutes - offsetMinutes;
  const endTotalMinutes = end.hours * 60 + end.minutes - offsetMinutes;

  // Determine day shift from start time
  let dayShift = 0;
  let normStartMinutes = startTotalMinutes;
  if (normStartMinutes < 0) {
    dayShift = -1;
    normStartMinutes += 24 * 60;
  } else if (normStartMinutes >= 24 * 60) {
    dayShift = 1;
    normStartMinutes -= 24 * 60;
  }

  let normEndMinutes = endTotalMinutes;
  if (normEndMinutes < 0) {
    normEndMinutes += 24 * 60;
  } else if (normEndMinutes >= 24 * 60) {
    normEndMinutes -= 24 * 60;
  }

  return {
    dayOfWeek: wrapDay(dayOfWeek + dayShift),
    startTime: formatTime(Math.floor(normStartMinutes / 60), normStartMinutes % 60),
    endTime: formatTime(Math.floor(normEndMinutes / 60), normEndMinutes % 60),
  };
}

// ─── public API ──────────────────────────────────────────────────────

/**
 * Convert a local-time weekly slot to UTC for storage.
 *
 * `refDate` samples the offset at a specific instant instead of the summer
 * `REF_DATE`. STORAGE callers must not pass it — stored UTC is REF_DATE-anchored
 * by convention and re-anchoring it would move what is already in the database.
 * It exists for DISPLAY callers that need to know what wall-clock a recurring
 * slot actually falls at on a given date: this is the COURSE leg, and without it
 * a `courseTz`→viewer label re-samples only the viewer's offset, which is
 * exactly nothing for a viewer in a zone that does not change its clocks.
 * Default preserves today's behavior for every existing caller.
 *
 * @example
 * localTimeToUtc(0, '14:00', '15:00', 'America/New_York')
 * // → { dayOfWeek: 0, startTime: '18:00', endTime: '19:00' }  (EDT, offset -240)
 *
 * localTimeToUtc(0, '22:00', '23:00', 'America/New_York')
 * // → { dayOfWeek: 1, startTime: '02:00', endTime: '03:00' }  (rolls to Tuesday UTC)
 */
export function localTimeToUtc(
  dayOfWeek: number,
  startTime: string,
  endTime: string,
  ianaTz: string,
  refDate?: Date,
): WeeklySlot {
  const offset = getUtcOffsetMinutes(ianaTz, refDate);
  return convertSlot(dayOfWeek, startTime, endTime, offset);
}

/**
 * Convert a UTC weekly slot back to local time for display.
 *
 * Pass `refDate` to sample the timezone offset at a specific session instant
 * (needed for DST-correct round-trips from REF_DATE-anchored UTC back to the
 * admin's typed wall-clock — section-instance materialization relies on this).
 * Default preserves the legacy summer-anchored behavior for existing callers.
 *
 * @example
 * utcTimeToLocal(0, '18:00', '19:00', 'America/New_York')
 * // → { dayOfWeek: 0, startTime: '14:00', endTime: '15:00' }  (EDT)
 */
export function utcTimeToLocal(
  dayOfWeek: number,
  startTime: string,
  endTime: string,
  ianaTz: string,
  refDate?: Date,
): WeeklySlot {
  const offset = getUtcOffsetMinutes(ianaTz, refDate);
  // To go UTC→local we ADD the offset (opposite direction)
  return convertSlot(dayOfWeek, startTime, endTime, -offset);
}

/**
 * Re-bake a stored-UTC weekly slot for a user's timezone change, preserving
 * WALL-CLOCK time. The slot currently reads correctly in `oldTz`; after this
 * returns, its stored UTC is shifted by the old→new offset delta so it still
 * projects to the same local wall-clock time in `newTz`.
 *
 * Pure composition of `utcTimeToLocal(…oldTz)` → `localTimeToUtc(…newTz)` — it
 * reuses `convertSlot`'s day-of-week-wrap logic and introduces no new offset
 * math. Both legs anchor to the same summer `REF_DATE`, so the round-trip is
 * internally consistent with how slots are stored and displayed everywhere.
 *
 * Used by tech-spec-timezone-change-availability-rebake (the DB loop lives in
 * `availability-timezone-rebake.service.ts`).
 *
 * @example
 * // A 09:00–10:00 Sunday slot baked under NY (-240), re-baked to Chicago (-300):
 * rebakeWeeklySlot(0, '13:00', '14:00', 'America/New_York', 'America/Chicago')
 * // → { dayOfWeek: 0, startTime: '14:00', endTime: '15:00' }  (still 09:00 local)
 */
export function rebakeWeeklySlot(
  dayOfWeek: number,
  startTime: string,
  endTime: string,
  oldTz: string,
  newTz: string,
): WeeklySlot {
  const local = utcTimeToLocal(dayOfWeek, startTime, endTime, oldTz);
  return localTimeToUtc(local.dayOfWeek, local.startTime, local.endTime, newTz);
}

// ─── Section-instance materialization helpers ──────────────────────────
//
// These two helpers exist for the per-instance UTC cache built in spec
// `tech-spec-section-instances-foundation`. They are intentionally narrow:
// `enumerateSessionDates` walks calendar dates in the course's IANA zone
// and yields the YYYY-MM-DD strings whose wall-clock day-of-week matches;
// `composeInstantInTz` then converts each `(date, wall-clock HH:MM)` pair
// into a concrete UTC `Date` using the offset sampled at noon-of-target-date
// (DST behavior locked by `tzdata-parity.test.ts`).

export class TimezoneUtilsError extends Error {
  constructor(
    public readonly code: 'INVALID_TIMEZONE',
    message: string,
  ) {
    super(message);
    this.name = 'TimezoneUtilsError';
  }
}

/**
 * Refuse a timezone string `Intl` would not accept, as a `TimezoneUtilsError`
 * rather than the raw `RangeError` it would otherwise surface as.
 *
 * Exported because `todayDateInTimezone` / `dateInTimezone` do NOT call it —
 * they hand the string straight to `Intl.DateTimeFormat` — so a caller that
 * projects a stored `courses.timezone` (validated only at the app layer) has to
 * guard it first. `enumerateSessionDates` and `composeInstantInTz` call it
 * themselves.
 */
export function assertValidIanaTz(ianaTz: string): void {
  if (!ianaTz || typeof ianaTz !== 'string') {
    throw new TimezoneUtilsError('INVALID_TIMEZONE', `ianaTz is empty`);
  }
  try {
    // Throws RangeError if the tz isn't a recognized IANA name.
    new Intl.DateTimeFormat('en-US', { timeZone: ianaTz });
  } catch {
    throw new TimezoneUtilsError(
      'INVALID_TIMEZONE',
      `ianaTz "${ianaTz}" is not a valid IANA timezone`,
    );
  }
}

/**
 * Walk calendar dates in `courseTz` from `termStart` (inclusive) to `termEnd`
 * (inclusive) and yield YYYY-MM-DD strings whose course-tz day-of-week equals
 * `wallClockDow`. The calendar grid is `courseTz`-anchored — termStart/End
 * are projected to their course-tz calendar date before the walk begins.
 *
 * Returns an empty array (not an error) if `termEnd < termStart` so callers
 * that supply an empty / inverted term don't crash; throw on invalid tz.
 */
export function enumerateSessionDates(
  termStart: Date,
  termEnd: Date,
  wallClockDow: number,
  courseTz: string,
): string[] {
  assertValidIanaTz(courseTz);
  return enumerateSessionDatesBetween(
    todayDateInTimezone(courseTz, termStart),
    todayDateInTimezone(courseTz, termEnd),
    wallClockDow,
    courseTz,
  );
}

/**
 * The same weekly walk, between two dates that are ALREADY course-tz calendar
 * days (`YYYY-MM-DD`).
 *
 * ⚠️ THIS SEAM EXISTS TO MAKE A FRAME MISMATCH UNREPRESENTABLE. A run window can
 * arrive from two places in two frames: `terms.start_date`/`end_date` are UTC
 * instants, while `classes.start_date`/`end_date` and
 * `class_sections.start_date`/`end_date` are already course-tz calendar dates.
 * Handing a calendar date to a function that projects it — or an instant to one
 * that does not — is a silent ONE-DAY error, and only WEST of UTC, so a fixture
 * on an Asian course would never catch it.
 *
 * So each caller enters in its own frame: `enumerateSessionDates` above takes
 * instants and projects; this takes days and does not. Neither can be handed the
 * other's input without a type error.
 */
export function enumerateSessionDatesBetween(
  startStr: string,
  endStr: string,
  wallClockDow: number,
  courseTz: string,
): string[] {
  assertValidIanaTz(courseTz);
  if (endStr < startStr) return [];

  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const dates: string[] = [];

  // Anchor walk in UTC noon to dodge DST-edge `getUTCDay()` flakes — the
  // course-tz calendar dow of `2026-03-08` is the same whether we sample
  // it at 00:00 or 12:00 UTC, but UTC noon never sits inside a phantom
  // hour the way local 02:30 would.
  let cursor = Date.UTC(sy, sm - 1, sd, 12, 0, 0);
  const endUtc = Date.UTC(ey, em - 1, ed, 12, 0, 0);

  while (cursor <= endUtc) {
    const cursorDate = new Date(cursor);
    const courseTzDow = todayInTimezone(courseTz, cursorDate);
    if (courseTzDow === wallClockDow) {
      dates.push(todayDateInTimezone(courseTz, cursorDate));
    }
    cursor += 24 * 60 * 60 * 1000;
  }
  return dates;
}

/**
 * Given a course-tz YYYY-MM-DD and a wall-clock HH:MM, return the UTC `Date`
 * representing that wall-clock moment in `ianaTz`. Samples the offset at
 * noon-of-target-date — see spec Codebase Patterns for DST-edge semantics.
 * Behavior locked by `tzdata-parity.test.ts` against Node 24 / ICU 77.1.
 */
export function composeInstantInTz(
  courseTzDate: string,
  wallClockTime: string,
  ianaTz: string,
): Date {
  assertValidIanaTz(ianaTz);

  const [y, mo, d] = courseTzDate.split('-').map(Number);
  const [h, mi] = wallClockTime.split(':').map(Number);
  const refDateAtNoon = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  const offsetMinutes = getUtcOffsetMinutes(ianaTz, refDateAtNoon);
  return new Date(Date.UTC(y, mo - 1, d, h, mi) - offsetMinutes * 60_000);
}

/**
 * Format a time string for display with 12-hour format. Drops the `:00`
 * suffix for whole-hour times.
 *
 * @example
 * formatTimeDisplay('14:00') // → '2 PM'
 * formatTimeDisplay('09:30') // → '9:30 AM'
 */
export function formatTimeDisplay(hhmm: string): string {
  const { hours, minutes } = parseTime(hhmm);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMin = minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`;
  return `${displayHour}${displayMin} ${period}`;
}

/**
 * Format a time for display with timezone abbreviation. Drops the `:00`
 * suffix for whole-hour times.
 *
 * @example
 * formatTimeWithZone('14:00', 'America/New_York') // → '2 PM ET'
 * formatTimeWithZone('09:00', 'Asia/Tokyo')       // → '9 AM JST'
 */
export function formatTimeWithZone(hhmm: string, ianaTz: string): string {
  return `${formatTimeDisplay(hhmm)} ${getTimezoneAbbreviation(ianaTz)}`;
}

/**
 * Project a UTC weekly slot into a viewer's timezone and return the parts
 * needed for display. Plain function (not a hook) — use this inside `.map()`
 * callbacks where calling the React hook per row would violate rules-of-
 * hooks. Pull viewer timezone from `useAuthStore` at the component level
 * and pass it in.
 */
export function projectLocalTimeRange(
  dayOfWeek: number,
  startTimeUtc: string,
  endTimeUtc: string,
  viewerTimezone: string,
  refDate?: Date,
): {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  tz: string;
  label: string;
} {
  const local = utcTimeToLocal(dayOfWeek, startTimeUtc, endTimeUtc, viewerTimezone, refDate);
  const tz = getTimezoneAbbreviation(viewerTimezone);
  const startTime = formatTimeDisplay(local.startTime);
  const endTime = formatTimeDisplay(local.endTime);
  return {
    dayOfWeek: local.dayOfWeek,
    startTime,
    endTime,
    tz,
    label: `${startTime}–${endTime} ${tz}`,
  };
}

/**
 * Project a real session INSTANT into a viewer's timezone.
 *
 * ⚠️ This is the one to reach for whenever the session's actual moment is
 * known. `projectLocalTimeRange` takes the REF_DATE-anchored weekly triple off
 * `class_sections` — a summer wall-clock frozen into UTC at section-write time
 * — and re-samples the VIEWER's offset at whatever date you hand it. On a
 * winter date that subtracts the viewer's DST hour from a value that never had
 * the course's DST hour in it, so the label reads an hour early: an 8 PM ET
 * Thursday class (stored Fri 00:00 UTC) rendered "7 PM ET" for every date
 * after 1 Nov. `section_instances.start_utc` is the real moment, materialized
 * per session and DST-correct by construction, so projecting it needs no
 * reference date and cannot drift.
 *
 * Returns the viewer-local date and weekday alongside the label, because an
 * instant that crosses midnight in the viewer's zone belongs to a different
 * calendar day than the one the caller was holding.
 *
 * @example
 * // Thu 12 Nov 2026, 8 PM EST = Fri 01:00 UTC
 * projectInstantRange(
 *   new Date('2026-11-13T01:00:00Z'),
 *   new Date('2026-11-13T01:45:00Z'),
 *   'America/New_York',
 * )
 * // → { dayOfWeek: 4, date: '2026-11-12', label: '8 PM–8:45 PM ET', ... }
 */
export function projectInstantRange(
  startUtc: Date,
  endUtc: Date,
  viewerTimezone: string,
): {
  dayOfWeek: number;
  date: string;
  startTime: string;
  endTime: string;
  /** 24-hour "HH:MM", for callers that format (or compare) the raw value. */
  startTime24: string;
  endTime24: string;
  tz: string;
  label: string;
} {
  const start = wallClockInTimezone(startUtc, viewerTimezone);
  const end = wallClockInTimezone(endUtc, viewerTimezone);
  const tz = getTimezoneAbbreviation(viewerTimezone);
  const startTime = formatTimeDisplay(start.time);
  const endTime = formatTimeDisplay(end.time);
  return {
    dayOfWeek: start.dayOfWeek,
    date: start.date,
    startTime,
    endTime,
    startTime24: start.time,
    endTime24: end.time,
    tz,
    label: `${startTime}\u2013${endTime} ${tz}`,
  };
}

/** Wall-clock date, weekday and "HH:MM" an instant reads as in `iana`. */
function wallClockInTimezone(
  instant: Date,
  iana: string,
): { date: string; dayOfWeek: number; time: string } {
  const { year, month, day } = getDatePartsInTimezone(iana, instant);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(instant);
  const get = (type: string) =>
    parseInt(parts.find((p) => p.type === type)!.value, 10);
  // Midnight is reported as 24 by some ICU builds.
  const hours = get('hour') === 24 ? 0 : get('hour');
  return {
    date: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    // Noon-of-day anchor, so getUTCDay() never lands inside a transition.
    dayOfWeek: new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).getUTCDay(),
    time: formatTime(hours, get('minute')),
  };
}

/**
 * Get a short timezone abbreviation for an IANA timezone.
 *
 * @example
 * getTimezoneAbbreviation('America/New_York') // → 'ET'
 * getTimezoneAbbreviation('America/Los_Angeles') // → 'PT'
 * getTimezoneAbbreviation('Asia/Tokyo') // → 'JST'
 */
export function getTimezoneAbbreviation(ianaTz: string): string {
  // Use Intl to get the short timezone name
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTz,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(REF_DATE);
  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  if (!tzPart) return ianaTz;

  const raw = tzPart.value;

  // Common US zones: convert EDT/EST→ET, CDT/CST→CT, MDT/MST→MT, PDT/PST→PT
  const genericMatch = raw.match(/^([A-Z])[DS]([A-Z])$/);
  if (genericMatch) {
    return `${genericMatch[1]}${genericMatch[2]}`;
  }

  // Node on some platforms returns "GMT+X" for non-US zones — keep as-is
  return raw;
}

/**
 * Return the day-of-week (0=Sun..6=Sat) for the calendar day that `now`
 * falls in for the supplied IANA timezone.
 *
 * Critical for any code that compares a stored UTC `dayOfWeek` against
 * "today" for a specific viewer — the server clock is UTC in prod, so
 * `new Date().getDay()` would return the UTC day, not the viewer's day.
 *
 * @example
 * todayInTimezone('America/Los_Angeles', new Date('2026-06-02T04:00:00Z'))
 * // → 1 (Mon — 9pm PT, even though UTC clock says Tue)
 */
export function todayInTimezone(iana: string, now: Date = new Date()): number {
  const { year, month, day } = getDatePartsInTimezone(iana, now);
  // Anchor at noon-of-day in UTC to dodge DST-edge transitions when calling getUTCDay().
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).getUTCDay();
}

/**
 * Return `YYYY-MM-DD` for the calendar day that `now` falls in for the
 * supplied IANA timezone.
 *
 * @example
 * todayDateInTimezone('America/Los_Angeles', new Date('2026-06-01T23:30:00Z'))
 * // → '2026-06-01' (still Sun 16:30 PT)
 *
 * todayDateInTimezone('Asia/Shanghai', new Date('2026-06-01T16:30:00Z'))
 * // → '2026-06-02' (already Tue 00:30 BJT)
 */
export function todayDateInTimezone(iana: string, now: Date = new Date()): string {
  return dateInTimezone(now, iana);
}

/**
 * Return `YYYY-MM-DD` for the calendar day an INSTANT falls on in the supplied
 * IANA timezone.
 *
 * Use this — never `instant.toISOString().slice(0, 10)` — whenever a stored UTC
 * moment has to be shown to a human as a date. `section_instances.session_date`
 * is the UTC date of `start_utc` (the D2 schema convention), so for any section
 * that starts at or after 20:00 ET it names the day AFTER the one the family
 * sees on their calendar: a Wednesday 8 PM ET class is stored as the Thursday.
 * That value is the right key for attendance rows and confirm deep links, and
 * the wrong thing to print.
 *
 * @example
 * dateInTimezone(new Date('2026-09-03T00:00:00Z'), 'America/New_York')
 * // → '2026-09-02' (Wed 8pm ET, though the UTC date is Thu the 3rd)
 */
export function dateInTimezone(instant: Date, iana: string): string {
  const { year, month, day } = getDatePartsInTimezone(iana, instant);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getDatePartsInTimezone(
  iana: string,
  now: Date,
): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(now);
  const get = (type: string) =>
    parseInt(parts.find((p) => p.type === type)!.value, 10);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * An enrollment deadline, named as the DAY an admin actually picked.
 *
 * `terms.enrollment_close_date` is composed by `term.service.ts ›
 * resolveEnrollmentCloseInstant` as END-OF-DAY in the term's PROGRAM timezone.
 * Reading the day back therefore has to use that same zone.
 *
 * ⚠️ NOT `.toISOString().slice(0, 10)`. The instant's UTC day is the day AFTER
 * the chosen one for every program west of UTC — including `America/New_York`,
 * the default — so a NY admin setting "Sep 1" would have had every reminder
 * tell families "before 2026-09-02". This could not surface before
 * tech-spec-enrollment-close-date: the column was always null and every
 * reminder took its "the deadline" fallback.
 *
 * Returns null for no deadline, so callers can keep that fallback.
 */
export function closeDayInProgramTz(
  closeDate: Date | string | null | undefined,
  ianaTz: string | null | undefined,
): string | null {
  if (!closeDate) return null;
  const instant = closeDate instanceof Date ? closeDate : new Date(closeDate);
  if (Number.isNaN(instant.getTime())) return null;
  try {
    return todayDateInTimezone(ianaTz && ianaTz.trim() ? ianaTz : 'UTC', instant);
  } catch {
    // A zone this broken should never reach here — activation refuses to SET a
    // deadline against one — and a reminder must not fail over formatting.
    return instant.toISOString().slice(0, 10);
  }
}

/**
 * Get the full display name for a timezone.
 *
 * @example
 * getTimezoneDisplayName('America/New_York') // → 'Eastern Time'
 */
export function getTimezoneDisplayName(ianaTz: string): string {
  const abbr = getTimezoneAbbreviation(ianaTz);
  const names: Record<string, string> = {
    ET: 'Eastern Time',
    CT: 'Central Time',
    MT: 'Mountain Time',
    PT: 'Pacific Time',
    GMT: 'Greenwich Mean Time',
    UTC: 'Coordinated Universal Time',
  };
  return names[abbr] ?? ianaTz.replace(/_/g, ' ').replace(/^.*\//, '');
}

// Re-export the offset helper for testing
export { getUtcOffsetMinutes as _getUtcOffsetMinutes };
